import React, { useEffect, useState } from "react";
import Select, { Props } from "react-select";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { styled } from "@mui/system";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { components } from "react-select";
import CreatableSelect from "react-select/creatable";

interface DynamicSelectProps extends Props<any> {
  label: string;
  description?: React.ReactNode;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  isRequired?: boolean;
  error?: string;
  isDisabled?: boolean;
  ddWidth?: string;
  ddHeight?: string;
  defaultPlaceholder?: string;
  page?: number;
  creatable?: boolean;
  /** Force compact dashboard select styles; defaults to true on dashboard routes */
  dashboardStyle?: boolean;
}

const isDashboardPath = () => {
  const path = window.location.pathname || "";
  return (
    path.includes("/analytics") ||
    path.includes("/accounts-summary") ||
    path.includes("/kpis")
  );
};

const DescriptionText = ({ description }: { description: React.ReactNode }) => {
  const { i18n } = useTranslation();
  return (
    <Typography
      className="font-Saira"
      sx={{
        fontSize: '11px',
        color: "#111827",
        padding: "10px",
        maxWidth: "280px",
      }}
      dir={i18n.language === "ar" || i18n.language === "ur" ? "rtl" : "ltr"}
    >
      {description}
    </Typography>
  );
};

const DynamicSelect: React.FC<DynamicSelectProps> = ({
  error,
  label,
  description,
  isRequired,
  isDisabled,
  ddWidth,
  ddHeight,
  defaultPlaceholder,
  page,
  creatable,
  dashboardStyle,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const [placeholder, setPlaceholder] = useState(
    defaultPlaceholder || t("select"),
  );
  const [options, setOptions] = useState<any[]>([]);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const SelectComponent = creatable ? CreatableSelect : Select;
  const useDashboardStyle =
    dashboardStyle !== undefined ? dashboardStyle : isDashboardPath();

  useEffect(() => {
    setPlaceholder(defaultPlaceholder || "select");
  }, [i18n.language, defaultPlaceholder]);
  useEffect(() => {
    if (page === 1) setOptions((props?.options as any) || []);
    else {
      setOptions((prev) => {
        const newOptions = props.options || [];
        return [
          ...prev,
          ...newOptions.filter(
            (newOption) =>
              !prev.some((option) => option.value === newOption.value),
          ),
        ];
      });
    }
  }, [props.onMenuScrollToBottom]);

  const CustomMultiValue = (props: any) => (
    <Tooltip title={props.data.label}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          borderRadius: "8px",
          backgroundColor: "#f2f2f2",
          marginRight: "4px",
        }}
      >
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {props.data.label}
        </div>
        <CloseIcon
          fontSize="small"
          onClick={(e) => props.removeProps.onClick(e)}
          style={{ marginLeft: "4px", cursor: "pointer" }}
        />
      </div>
    </Tooltip>
  );
  const CustomOption = (props: any) => {
    return (
      <components.Option {...props}>
        <Tooltip title={props.data.label} arrow placement="right">
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              whiteSpace: "normal",
              lineHeight: "1.2em",
              maxHeight: "2.4em",
            }}
          >
            {props.data.label}
          </span>
        </Tooltip>
      </components.Option>
    );
  };
  const handleFocusAndBlur = (event: any) => {
    setPlaceholder(
      event.type === "focus" ? "" : defaultPlaceholder || t("select"),
    );
  };

  const isArabic = i18n.language === "ar" || i18n.language === "ur";
  const isFieldDisabledStyles: React.CSSProperties = isDisabled
    ? { pointerEvents: "none", opacity: 0.5 }
    : {};

  const formControlStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: 38,
      height: props.isMulti ? "auto" : 38,
      textAlign: "left" as const,
      borderRadius: "8px",
      borderWidth: "1px",
      borderColor: state.isFocused ? "black" : "#C4C4C4",
      backgroundColor: "white",
      boxShadow: "none",
      alignItems: "center",
      "&:hover": { borderColor: "black" },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "0 8px",
      height: props.isMulti ? "auto" : "100%",
      display: "flex",
      alignItems: "center",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      whiteSpace: "nowrap" as const,
      margin: 0,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      textAlign: "left" as const,
      margin: 0,
    }),
    input: (provided: any) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: props.isMulti ? "auto" : 36,
      alignSelf: "center",
      padding: 0,
      margin: 0,
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: 0,
      margin: 0,
      top: "auto",
      bottom: "auto",
      transform: "none",
      borderRadius: "8px",
      width: ddWidth || "36px",
      height: ddHeight || "36px",
      backgroundColor: themeColors?.button,
      color: "black",
      fill: "black",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "center",
      boxSizing: "border-box" as const,
      "& svg": {
        display: "block",
      },
      "&:hover svg": { fill: "black" },
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      padding: "0 4px",
      display: "flex",
      alignItems: "center",
      alignSelf: "center",
      top: "auto",
      transform: "none",
    }),
  };

  const dashboardControlStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      height: "auto",
      textAlign: "left" as const,
      borderRadius: "8px",
      borderWidth: "1px",
      borderColor: state.isFocused ? "black" : "#C4C4C4",
      backgroundColor: "white",
      boxShadow: "none",
      "&:hover": { borderColor: "black" },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      whiteSpace: "nowrap" as const,
    }),
    singleValue: (provided: any) => ({
      ...provided,
      textAlign: "left" as const,
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      borderRadius: "8px",
      width: ddWidth ? ddWidth : "100%",
      height: ddHeight ? ddHeight : "100%",
      backgroundColor: themeColors?.button,
      color: "black",
      fill: "black",
      alignItems: "center",
      "&:hover svg": { fill: "black" },
    }),
  };

  const sharedStyles = useDashboardStyle
    ? dashboardControlStyles
    : formControlStyles;

  const hasLabel = Boolean(label && String(label).trim());

  return (
    <div
      className="text-left commonSelect relative"
      style={isFieldDisabledStyles}
    >
      {(hasLabel || description) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            // gap: "4px",
            flexDirection: isArabic ? "row-reverse" : "row",
            justifyContent: isArabic ? "end" : "start",
            // mb: hasLabel ? "1px" : 0,
          }}
        >
          {hasLabel ? (
            <Typography
              className="font-Saira font-[600] text-[13px] text-txt-color line-clamp-1"
              style={{
                textAlign: isArabic ? "right" : "left",
              }}
              color={error && !isDisabled ? "red !important" : "textPrimary"}
            >
              {t(label)}
              {isRequired ? " *" : ""}
            </Typography>
          ) : null}
          {description && (
            <Tooltip
              title={<DescriptionText description={description} />}
              enterDelay={0}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#fff",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
                  },
                },
              }}
            >
              <InfoIcon
                sx={{ fontSize: 17, color: "#6b7280", cursor: "pointer" }}
              />
            </Tooltip>
          )}
        </Box>
      )}
      <SelectComponent
        {...props}
        className="font-Saira italic font-[500] text-[13px] pr-0 mt-[-1.8px]"
        menuPlacement="auto"
        menuPortalTarget={document.body}
        styles={{
          ...sharedStyles,
          option: (provided, state) => ({
            ...provided,
            textAlign: "left",
            backgroundColor: state.isSelected
              ? themeColors?.primary
              : state.isFocused
                ? "#f2f2f2"
                : provided.backgroundColor,
            "&:hover": {
              backgroundColor: state.isSelected
                ? themeColors?.primary
                : themeColors?.listHoverBg,
            },
          }),
          indicatorSeparator: (base) => ({
            ...base,
            display: "none",
          }),
          menu: (base) => ({
            ...base,
            ...(useDashboardStyle && {
              width: "fit-content",
              minWidth: "fit-content",
              right: 0,
              left: "auto",
            }),
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        components={{
          MultiValue: CustomMultiValue,
          Option: CustomOption,
        }}
        onFocus={(e) => {
          handleFocusAndBlur(e);
        }}
        onBlur={(e) => {
          handleFocusAndBlur(e);
        }}
        placeholder={t(placeholder)}
        loadingMessage={() => <h3>{t("loading") + "..."}</h3>}
        isDisabled={isDisabled}
        options={props.onMenuScrollToBottom ? options : props.options}
        formatCreateLabel={(inputValue: string) =>
          t("create") + ' "' + inputValue + '"'
        }
      />
      {!isDisabled && (
        <>
          <HelperText
            className="font-Saira italic font-[500] text-[11px] text-red-400 commonSelectError"
            error={Boolean(error)}
          >
            {error ? t(error) : null}
          </HelperText>
          <span
            className="dropdown"
            style={{
              display: "block",
              height: useDashboardStyle ? "10px" : 0,
              lineHeight: 0,
              overflow: "hidden",
            }}
          />
        </>
      )}
    </div>
  );
};

const HelperText = styled("p")<{ error?: boolean }>`
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.875rem;
  color: ${(props) => (props.error ? "red" : "")};
  text-align: left;
  position: absolute;
`;

export default DynamicSelect;
